import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Autocomplete } from '@material-ui/lab';
import { IconButton, TextField } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import moment from 'moment';

import { Button, Form, Typography } from 'shared/components';
import { InputField, SelectField } from 'shared/components/controls';
import { useStyles } from './studentLoginInstitutionForm.styles';
import { StudentLoginInstitutionFormProps } from './studentLoginInstitutionForm.types';
import { email, studentIdRequired } from 'shared/validators';
import { EventData, EventOption } from 'app/highSchoolSelfRegistration/selectInstituition/SelectInstituition.types';

export const StudentLoginInstitutionForm = ({
  onSubmit,
  instituitionEventDetails,
}: StudentLoginInstitutionFormProps) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();

  const eventDetails: EventData[] = Object.keys(instituitionEventDetails?.events).map(
    year =>
      ({
        year,
        events: instituitionEventDetails?.events[year as keyof typeof instituitionEventDetails.events] || [],
      } as EventData),
  );

  const yearOptions = eventDetails
    .sort((a, b) => Number(b.year) - Number(a.year)) // Convert year to a number
    .map(item => ({
      value: item.year,
      label: item.year,
    }));

  const [selectedYear, setSelectedYear] = useState(yearOptions.length === 1 ? yearOptions[0].value : '');
  const [selectedEvent, setSelectedEvent] = useState('');

  const eventOptions =
    eventDetails
      .find(item => item.year === selectedYear)
      ?.events.map(event => ({
        value: event.id,
        label: `${moment(`${event.startDateOnly} ${event.startTimeOnly}`).format('dddd, Do MMMM')} (${event.name})`,
        clipPurchaseType: event.clipPurchaseType,
        eventName: event.name,
      })) || [];

  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
    setSelectedEvent('');
  };

  const handleEventChange = (event: React.ChangeEvent<{}>, value: EventOption | null) => {
    if (value) {
      const selectedEventId = value.value;
      const selectedEventDetails = eventOptions.find(item => item.value === selectedEventId);

      if (selectedEventDetails) {
        setSelectedEvent(selectedEventDetails.value);
      }
    }
  };

  return (
    <div className={styles.formContainer}>
      <Form<object>
        onSubmit={(values, formApi) => {
          const submitValues = {
            ...values,
            eventId: selectedEvent,
          };
          onSubmit(submitValues, formApi);
        }}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate>
            <SelectField
              name="year"
              label="Select year"
              options={yearOptions}
              defaultValue={yearOptions.length === 1 ? yearOptions[0].value : ''}
              onClick={handleYearChange}
            />
            <span className={styles.eventText}>Select event</span>
            <Autocomplete
              className={styles.eventDropdown}
              popupIcon=""
              size="medium"
              options={eventOptions}
              getOptionLabel={option => option.label}
              value={eventOptions.find(option => option.value === selectedEvent) || null}
              onChange={handleEventChange}
              renderInput={params => (
                <TextField
                  {...params}
                  size="medium"
                  variant="outlined"
                  fullWidth
                  error={!selectedEvent}
                  helperText={!selectedEvent ? formatMessage({ id: 'av_user.student_login.event_required' }) : ''}
                />
              )}
              disabled={!selectedYear}
              closeIcon={
                <IconButton size="small" className={styles.closeIcon} onClick={() => setSelectedEvent('')}>
                  <Close />
                </IconButton>
              }
            />
            <input type="hidden" name="eventId" value={selectedEvent} />
            <Typography className={styles.labelTop}>{formatMessage({ id: 'av_user.student_login.email' })}</Typography>
            <InputField
              size="small"
              name="email"
              fullWidth
              type="email"
              placeholder="Enter your email"
              validate={[email()]}
              className={styles.textField}
              classes={{ root: styles.textFieldRoot }}
            />
            <Typography className={styles.studentID}>
              {formatMessage({ id: 'av_user.student_login.student_id' })}
            </Typography>
            <InputField
              size="small"
              name="studentId"
              fullWidth
              placeholder="Enter your student id"
              type="text"
              className={styles.textField}
              classes={{ root: styles.textFieldRoot }}
              validate={[studentIdRequired()]}
            />

            <Button variant="contained" color="primary" fullWidth type="submit" className={styles.buttonNext}>
              {formatMessage({ id: 'av_user.student_login.button_login' })}
            </Button>
          </form>
        )}
      />
    </div>
  );
};
